import * as React from 'react';

import {
  enumPurchaseLoadStatus,
  PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { LocalizedDateTime, Row, Widget } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';

import {
  $closeCreateSupplierDialog,
  $createSupplierInitialName,
  $isCreateSupplierDialogOpen,
  $onSupplierCreatedCallback,
} from '../../../_state/createSupplierDialog.state';
import { $scaleTicketTab } from '../../../_state/scaleTicketPro.state';
import { WeightUnitSwitch } from '../../_shared/WeightUnitSwitch/WeightUnitSwitch';
import { GoBackButton } from '../shared/GoBackButton';
import { PrintDocumentsMenu } from '../shared/PrintDocumentsMenu';
import { ScaleTicketSettingsPopover } from '../shared/ScaleTicketSettingsPopover';
import {
  DocumentNumber,
  TicketAccountDetailsRow,
  TicketDetailsRow,
  TicketTabsRow,
} from '../shared/TicketHeaderLayout';

import { InboundTicketHeaderActions } from './InboundTicketHeaderActions';
import { ScaleTicketStatusStepper } from './ScaleTicketStatusStepper';
import { TicketPurchaseLoadStatusBadge } from './TicketPurchaseLoadStatusBadge';

import { AccountCreateDialog } from '~/modules/crm/_components/accounts/AccountCreateDialog';
import { AccountFormState } from '~/modules/crm/_state/_types';
import {
  $ticketDetailsFormById,
  $updateTicketDetailsField,
  DEFAULT_DETAILS_FORM,
} from '~/modules/trade/_state/scaleTicketInboundInfoForm.state';
import { $inboundWeightUnitMode } from '~/modules/trade/_state/scaleTicketInboundMaterialsForm.state';
import {
  $ticketNotesFormById,
  $updateTicketNotesField,
  DEFAULT_NOTES_FORM,
} from '~/modules/trade/_state/scaleTicketInboundNotesForm.state';
import {
  $ticketSupplierFormById,
  $updateTicketSupplierSelectField,
  $updateTicketSupplierSelectFields,
  DEFAULT_SUPPLIER_FORM,
} from '~/modules/trade/_state/scaleTicketSupplierForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  PurchaseLoadPaymentRecord,
  PurchaseLoadRecord,
} from '~/modules/trade/types/purchaseLoad';
import {
  PrintableDocument,
  ScaleTicketTab,
} from '~/modules/trade/types/scaleTickets';
import { PagePresence } from '~/shared/components/PagePresence';
import { SelectFacilityTouch } from '~/shared/components/select-fields/SelectFacilityTouch';
import {
  SelectSupplierLocationPairTouch,
  type SelectableSupplierLocationPair,
} from '~/shared/components/select-fields/SelectSupplierLocationPairTouch';

type InboundTicketHeaderProps = {
  ticket: PurchaseLoadRecord | null;
  status?: PurchaseLoadStatus;
  materialCount?: number;
  isProcessing: boolean;
  grandTotal?: string;
  facilityId?: string;
  purchaseLoadId?: string;
  existingPayments?: PurchaseLoadPaymentRecord[];
  ticketReceivedDate?: string;
  printDocuments: PrintableDocument[];
  onHold: () => void;
  onVoid: (voidReason?: string) => void;
  onRelease: () => void;
  onPay: () => void;
  onGoBack: () => void;
};

//
// ⚛️ ⚛️ ⚛️ INBOUND TICKET HEADER ⚛️ ⚛️ ⚛️
//
export const InboundTicketHeader = ({
  ticket,
  status,
  materialCount,
  isProcessing,
  grandTotal,
  facilityId,
  purchaseLoadId,
  existingPayments,
  ticketReceivedDate,
  printDocuments,
  onHold,
  onVoid,
  onRelease,
  onPay,
  onGoBack,
}: InboundTicketHeaderProps) => {
  const t = useTranslations();

  // States
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [resolvedSupplierName, setResolvedSupplierName] = React.useState<
    string | undefined
  >(undefined);

  // Atoms
  const detailsFormMap = useAtomValue($ticketDetailsFormById);
  const notesFormMap = useAtomValue($ticketNotesFormById);
  const supplierFormMap = useAtomValue($ticketSupplierFormById);
  const isCreateDialogOpen = useAtomValue($isCreateSupplierDialogOpen);
  const createSupplierInitialName = useAtomValue($createSupplierInitialName);
  const onSupplierCreatedCallback = useAtomValue($onSupplierCreatedCallback);
  const queryClient = useAtomValue(queryClientAtom);
  const [tab, setTab] = useAtom($scaleTicketTab);
  const [weightUnitMode, setWeightUnitMode] = useAtom($inboundWeightUnitMode);
  const updateDetailsField = useSetAtom($updateTicketDetailsField);
  const updateNotesField = useSetAtom($updateTicketNotesField);
  const updateSupplierSelectField = useSetAtom(
    $updateTicketSupplierSelectField,
  );
  const updateSupplierSelectFields = useSetAtom(
    $updateTicketSupplierSelectFields,
  );
  const closeCreateSupplierDialog = useSetAtom($closeCreateSupplierDialog);

  // Memoized values
  const ticketId = ticket?.id;
  const detailsForm =
    (ticketId ? detailsFormMap.get(ticketId) : undefined) ??
    DEFAULT_DETAILS_FORM;
  const notesForm =
    (ticketId ? notesFormMap.get(ticketId) : undefined) ?? DEFAULT_NOTES_FORM;
  const supplierForm =
    (ticketId ? supplierFormMap.get(ticketId) : undefined) ??
    DEFAULT_SUPPLIER_FORM;
  const activeTab = (tab || 'materials') as ScaleTicketTab;
  const inventoryLocked =
    status === enumPurchaseLoadStatus.InventoryComplete ||
    status === enumPurchaseLoadStatus.PriceComplete ||
    status === enumPurchaseLoadStatus.Paid;
  const pricingLocked =
    status === enumPurchaseLoadStatus.PriceComplete ||
    status === enumPurchaseLoadStatus.Paid;
  const showMaterialControls = activeTab === 'materials' && !inventoryLocked;
  const supplierName =
    resolvedSupplierName ??
    ticket?.supplierAccount?.name ??
    t.scaleTicketPro.detail.unknownSupplier;
  const createdByLabel = ticket?.createdByUser
    ? `${ticket.createdByUser.firstName} ${ticket.createdByUser.lastName}`
    : null;

  const createAccountInitialValues = React.useMemo(
    (): Partial<AccountFormState> => ({
      name: createSupplierInitialName || undefined,
      relationship: {
        isCustomer: false,
        isSupplier: true,
        isCarrier: false,
      },
    }),
    [createSupplierInitialName],
  );

  // Callbacks
  const handleSupplierEntityResolved = React.useCallback(
    (entity: SelectableSupplierLocationPair) => {
      setResolvedSupplierName(entity.account?.name ?? undefined);
    },
    [],
  );

  const handleSupplierLocationChanged = React.useCallback(
    (id: string | undefined, entity?: SelectableSupplierLocationPair) => {
      if (!ticketId) return;
      setResolvedSupplierName(entity?.account?.name ?? undefined);
      updateSupplierSelectFields({
        updates: {
          supplierAccountId: entity?.accountId || null,
          supplierAddressId: id || null,
          purchaseOrderId: null,
          serviceId: null,
        },
        ticketId,
      });
    },
    [updateSupplierSelectFields, ticketId],
  );

  const handleFacilityChanged = React.useCallback(
    (value: string | undefined) => {
      if (!ticketId) return;
      updateSupplierSelectField({
        field: 'facilityId',
        value: value ?? null,
        ticketId,
      });
    },
    [updateSupplierSelectField, ticketId],
  );

  const handlePurchaseOrderChange = React.useCallback(
    (value: string | undefined) => {
      if (!ticketId) return;
      updateSupplierSelectField({
        field: 'purchaseOrderId',
        value: value ?? null,
        ticketId,
      });
    },
    [updateSupplierSelectField, ticketId],
  );

  const handleServiceChange = React.useCallback(
    (value: string | undefined) => {
      if (!ticketId) return;
      updateSupplierSelectField({
        field: 'serviceId',
        value: value ?? null,
        ticketId,
      });
    },
    [updateSupplierSelectField, ticketId],
  );

  const handleTabChange = React.useCallback(
    (t: string) => setTab(t as ScaleTicketTab),
    [setTab],
  );

  const handleWeightUnitChange = React.useCallback(
    (mode: string) => setWeightUnitMode(mode === 'scale' ? 'scale' : 'manual'),
    [setWeightUnitMode],
  );

  const handleAccountCreated = React.useCallback(
    (accountId: string) => {
      onSupplierCreatedCallback?.(accountId);
      closeCreateSupplierDialog();
      queryClient.invalidateQueries({
        queryKey: ['shared.selectSupplierAccount'],
      });
    },
    [onSupplierCreatedCallback, closeCreateSupplierDialog, queryClient],
  );

  const handleCreateDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) closeCreateSupplierDialog();
    },
    [closeCreateSupplierDialog],
  );

  return (
    <Widget className="@InboundTicketHeader mb-1 flex shrink-0 flex-col">
      {ticket?.documentNumber && (
        <div className="relative flex items-center px-3 pt-2 pb-1">
          <Row gap2 yCenter className="relative z-10">
            <DocumentNumber
              documentNumber={ticket.documentNumber?.toLocaleString()}
              relatedOrderDocumentNumber={ticket?.purchaseOrder?.documentNumber}
            />
            {status && <TicketPurchaseLoadStatusBadge status={status} />}
            {createdByLabel && (
              <span className="text-xs font-normal text-neutral/50">
                {createdByLabel}{' '}
                <LocalizedDateTime
                  date={ticket?.receivedDate}
                  dateStyle="relative-within-week"
                  timeStyle="short"
                />
              </span>
            )}
          </Row>
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            {status && (
              <ScaleTicketStatusStepper
                ticketId={ticket.id}
                status={status}
                isProcessing={isProcessing || isTransitioning}
                onTransitioningChange={setIsTransitioning}
                facilityId={facilityId}
              />
            )}
          </div>
          <div className="relative z-30 ml-auto">
            <ScaleTicketSettingsPopover />
          </div>
        </div>
      )}

      <Row yCenter xBetween gap2 className="px-1.5 pb-1.5">
        <GoBackButton onGoBack={onGoBack} isDisabled={isProcessing} />
        <TicketAccountDetailsRow avatarName={supplierName}>
          <SelectSupplierLocationPairTouch
            value={supplierForm.supplierAddressId}
            isDisabled={pricingLocked}
            size="xl"
            onEntityResolved={handleSupplierEntityResolved}
            onChange={handleSupplierLocationChanged}
            placeholder={
              t.scaleTicketPro.detail.selectSupplierLocationPlaceholder
            }
          />

          <SelectFacilityTouch
            value={supplierForm.facilityId}
            size="xl"
            isDisabled={pricingLocked}
            onChange={handleFacilityChanged}
            placeholder={t.scaleTicketPro.detail.selectFacilityPlaceholder}
          />
        </TicketAccountDetailsRow>

        <Row gap2 yCenter xEnd>
          <PrintDocumentsMenu
            documents={printDocuments}
            isDisabled={isProcessing}
          />

          {status && (
            <InboundTicketHeaderActions
              status={status}
              isProcessing={isProcessing || isTransitioning}
              ticketId={ticketId}
              grandTotal={grandTotal}
              facilityId={facilityId}
              purchaseLoadId={purchaseLoadId}
              existingPayments={existingPayments}
              ticketReceivedDate={ticketReceivedDate}
              onHold={onHold}
              onVoid={onVoid}
              onRelease={onRelease}
              onPay={onPay}
              showStepper={false}
            />
          )}
        </Row>
      </Row>

      <TicketDetailsRow
        fields={detailsForm}
        ticket={ticket}
        onUpdateFields={updateDetailsField}
        notesFields={notesForm}
        onUpdateNotesField={updateNotesField}
        purchaseOrderId={supplierForm.purchaseOrderId}
        serviceId={supplierForm.serviceId}
        supplierAccountId={supplierForm.supplierAccountId}
        supplierAddressId={supplierForm.supplierAddressId}
        facilityId={supplierForm.facilityId}
        showPurchaseOrder
        showService
        purchaseOrderDisabled={pricingLocked}
        serviceDisabled={pricingLocked}
        onPurchaseOrderChange={handlePurchaseOrderChange}
        onServiceChange={handleServiceChange}
      />

      <TicketTabsRow
        context="inbound"
        materialCount={materialCount}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        rightContent={
          <>
            <PagePresence />
            {showMaterialControls && (
              <WeightUnitSwitch
                value={weightUnitMode === 'scale' ? 'scale' : 'price'}
                onChange={handleWeightUnitChange}
              />
            )}
          </>
        }
      />

      <AccountCreateDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={handleCreateDialogOpenChange}
        onAccountCreated={handleAccountCreated}
        initialValues={createAccountInitialValues}
      />
    </Widget>
  );
};
